import { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiInstagram, FiTwitter, FiHeart, FiArrowUp, FiMapPin } from 'react-icons/fi';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const FooterContainer = styled(motion.footer)`
  background: linear-gradient(180deg, #1a1034 0%, #0f0a22 100%);
  padding: 4rem 2rem 2rem;
  position: relative;
  overflow: hidden;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const DecorativeCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(108, 40, 217, 0.1), rgba(255, 77, 141, 0.1));
  filter: blur(40px);
  z-index: 0;
  animation: ${float} 8s infinite ease-in-out;
  
  &:nth-child(1) {
    width: 200px;
    height: 200px;
    top: -100px;
    left: -100px;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    width: 250px;
    height: 250px;
    bottom: -125px;
    right: -125px;
    animation-delay: 2s;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const BrandSection = styled.div`
  h3 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(45deg, #ff4d8d, #6c28d9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    color: #cbd5e1;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    max-width: 300px;
    
    @media (max-width: 768px) {
      max-width: none;
    }
  }
`;

const LinksSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
  
  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: white;
    grid-column: 1 / -1;
  }
`;

const FooterLink = styled(motion.a)`
  color: #cbd5e1;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: #ff4d8d;
    transform: translateX(5px);
  }
`;

const ContactSection = styled.div`
  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: white;
  }
  
  p {
    color: #cbd5e1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      justify-content: center;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background: linear-gradient(45deg, #6c28d9, #ff4d8d);
    color: white;
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 1.2rem;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(108, 40, 217, 0.4);
    }
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 3rem auto 0;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: #cbd5e1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #ff4d8d;
    animation: ${pulse} 2s infinite;
  }
`;

const BackToTop = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(45deg, #6c28d9, #ff4d8d);
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(108, 40, 217, 0.4);
  }
`;

function Footer() {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <FooterContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <DecorativeCircle />
      <DecorativeCircle />
      
      <FooterContent>
        <BrandSection>
          <h3>RoseCity Web3</h3>
          <p>
            Empowering artists, creators, and entrepreneurs through blockchain technology and digital innovation.
          </p>
        </BrandSection>

        <LinksSection>
          <h4>Quick Links</h4>
          <FooterLink
            href="/about"
            whileHover={{ x: 5 }}
          >
            About Us
          </FooterLink>
          <FooterLink
            href="/support"
            whileHover={{ x: 5 }}
          >
            Support
          </FooterLink>
        </LinksSection>

        <ContactSection>
          <h4>Contact Us</h4>
          <p>
            <FiMapPin />
            Portland, Oregon
          </p>
          
          <h4 style={{ marginTop: '1.5rem' }}>Follow Us</h4>
          <SocialLinks>
            <motion.a
              href="https://www.instagram.com/rosecity_web3/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiInstagram />
            </motion.a>
            
            <motion.a
              href="https://x.com/RoseCityWeb3"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiTwitter />
            </motion.a>
          </SocialLinks>
        </ContactSection>
      </FooterContent>

      <FooterBottom>
        <Copyright>
          &copy; 2025 RoseCity Web3. Made with <FiHeart /> in Portland.
        </Copyright>
        
        {scrollPosition > 300 && (
          <BackToTop
            onClick={scrollToTop}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <FiArrowUp />
            Back to Top
          </BackToTop>
        )}
      </FooterBottom>
    </FooterContainer>
  );
}

export default Footer;